// ==============================================================================================
// LES SAUVEGARDES, PAR HÔTE — « est-ce que ça tourne encore ? »
//
// ⚠️ 🔴 CET ÉCRAN N'EXISTE PAS POUR MONTRER DES SUCCÈS, MAIS POUR RENDRE LES ABSENCES VISIBLES. Une
// sauvegarde qui s'arrête ne produit NI erreur, NI alerte, NI rien : elle cesse d'exister, et on le
// découvre le jour où on la cherche — c'est-à-dire le seul jour où elle comptait. C'est l'inverse
// d'une migration, dont l'échec est immédiat et bruyant.
//
// ⚠️ QUATRE ÉTATS, ET AUCUN NE SE LIT « TOUT VA BIEN » :
//   · JAMAIS RELEVÉ — la machine n'a rien envoyé. Ce n'est pas « en attente », c'est une question
//     posée à un humain : soit l'agent n'est pas posé, soit il ne parle plus depuis le début ;
//   · PÉRIMÉ — le dernier passage est trop vieux. ⚠️ C'est le cas le plus traître, parce que le
//     compte rendu dit « réussi » : un succès ANCIEN rassure alors qu'il devrait inquiéter ;
//   · EN ÉCHEC — le dernier passage a échoué, avec son motif RÉEL (§23.0) ;
//   · À JOUR — et même là, l'écran dit si les archives QUITTENT la machine, parce qu'une sauvegarde
//     posée à côté des données ne protège pas de la perte de la VM, qui est le scénario contre lequel
//     on se sauvegarde.
//
// ⚠️ 🔴 ET LE DÉTAIL BASE PAR BASE, PARCE QU'UN TOTAL MASQUE UNE ABSENCE. « 25 archives, 468 Mo »
// répond à « est-ce que ça tourne » ; ça ne répond jamais à « CE magasin est-il sauvegardé », qui est
// la seule question du jour où un magasin s'effondre. Cinq bases attendues, quatre sauvegardées : le
// total reste plausible, le poids aussi, et le magasin manquant ne se voit nulle part.
//
// ⚠️ JUMEAU DE L'ÉCRAN D'ESPACE DISQUE, à dessein : même forme, même vocabulaire, même façon de dire
// ce qui manque — et le détail par BASE y est ce que le détail par POINT DE MONTAGE est là-bas. Deux
// suivis qui se ressemblent se lisent d'un coup d'œil ; deux suivis dessinés différemment obligent à
// réapprendre à chaque fois.
// ==============================================================================================
import { EmptyState } from '@orgazone/ui-web';
import { type BackupHost, type BackupState } from '../api';

/** Octets → « 94 Mo ». Base 1000, comme `df -h` — et comme l'écran d'espace disque juste à côté. */
export function fmtOctets(n: number): string {
  const u = ['o', 'ko', 'Mo', 'Go', 'To'];
  let v = n;
  let i = 0;
  while (v >= 1000 && i < u.length - 1) { v /= 1000; i += 1; }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

/** Ce que chaque état DIT, et de quelle couleur. Écrit une fois : l'écran ne réinvente pas les mots. */
const ETATS: Record<BackupHost['state'], { mot: string; ton: string }> = {
  jamais: { mot: 'jamais relevé', ton: 'var(--wa-fg)' },
  perime: { mot: 'périmé', ton: 'var(--da-fg)' },
  echec: { mot: 'en échec', ton: 'var(--da-fg)' },
  ok: { mot: 'à jour', ton: 'var(--ok-fg)' },
};

function Hote({ h }: { h: BackupHost }) {
  const e = ETATS[h.state];
  return (
    <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-ctrl)', padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800 }}>{h.host}</span>
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: e.ton, fontWeight: 700 }}>{e.mot}</span>
        <div style={{ flex: 1 }} />
        {h.at && (
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>
            {new Date(h.at).toLocaleString('fr-FR')}
          </span>
        )}
      </div>

      {/* ⚠️ UN HÔTE MUET DIT QUOI FAIRE. « Jamais relevé » sans la marche à suivre laisse un écran qui
          constate ; ici il envoie poser la tâche. */}
      {h.state === 'jamais' && (
        <div className="mono" style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
          Aucun compte rendu de cette machine. La sauvegarde se pose avec
          {' '}<span style={{ color: 'var(--ink700)' }}>deploy/sauvegarder-bases.sh</span>{' '}
          (secret dans /etc/orgazone-sauvegardes.env).
        </div>
      )}

      {h.state === 'perime' && (
        // ⚠️ LE PIÈGE NOMMÉ : le dernier compte rendu peut dire « réussi ». C'est son ÂGE qui alarme.
        <div className="mono" style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }}>
          Aucune sauvegarde depuis plus d’un jour — la tâche de cette machine est probablement arrêtée.
          Les chiffres ci-dessous sont ceux du dernier passage.
        </div>
      )}

      {h.error && (
        // Le motif RÉEL, jamais un code générique (§23.0).
        <div className="mono" style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }}>
          {h.error}
        </div>
      )}

      {h.at && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          {h.archives != null && (
            <span className="mono" style={{ fontSize: 20, fontWeight: 800 }}>{h.archives}</span>
          )}
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
            {h.archives != null ? 'archive(s)' : 'nombre d’archives non mesuré'}
            {h.bytes != null && ` · ${fmtOctets(h.bytes)}`}
          </span>
          <div style={{ flex: 1 }} />
          {/* ⚠️ « SUR LA MACHINE » EST UN AVERTISSEMENT, PAS UNE INFORMATION DE RANGEMENT : des archives
              posées à côté des données disparaissent avec la VM. */}
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: h.offsite ? 'var(--ok-fg)' : 'var(--wa-fg)' }}>
            {h.offsite ? 'copiées hors de la machine' : 'SUR LA MACHINE — ne survit pas à sa perte'}
          </span>
        </div>
      )}

      {h.destination && (
        <div className="mono" style={{ marginTop: 6, fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>
          {h.destination}
        </div>
      )}

      {h.at && <AgentLine h={h} />}
      {h.at && <Detail h={h} />}
    </div>
  );
}

/**
 * CE QUI A PRODUIT CE PASSAGE — la version de l'agent, et d'où venait sa configuration.
 *
 * ⚠️ 🔴 UN AGENT PÉRIMÉ SAUVEGARDE TOUTES LES NUITS SANS ÉCHOUER. Il fait son travail, l'ancien, et
 * rien ne le distingue d'un agent à jour — alors que c'est lui qu'on croit capable de récupérer les
 * données. C'est la Forme T appliquée à la pièce la plus critique du dispositif.
 *
 * ⚠️ ET LA SOURCE DE CONFIGURATION SE DIT, parce qu'un agent qui n'a pas joint la Console a appliqué
 * son réglage LOCAL : afficher la destination déclarée montrerait le nouveau chemin pendant que les
 * fichiers sont partis à l'ancien (§23.0).
 */
function AgentLine({ h }: { h: BackupHost }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
      <span
        className="mono"
        style={{
          fontSize: 'var(--t-micro)', padding: '2px 7px', borderRadius: 5,
          background: h.agentStale ? 'var(--wa-bg)' : 'var(--ne-bg)',
          color: h.agentStale ? 'var(--wa-fg)' : 'var(--ink500)',
          fontWeight: h.agentStale ? 700 : 400,
        }}
      >
        {/* ⚠️ « n’a pas dit sa version » plutôt que « inconnue » : la mesure existe, donc se taire
            désigne un agent ANTÉRIEUR — un fait, pas une ignorance. */}
        {h.agentVersion ? `agent v${h.agentVersion}` : 'agent sans version'}
        {h.agentStale && ' — À METTRE À JOUR'}
      </span>

      {h.configSource && (
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: h.configSource === 'console' ? 'var(--ink500)' : 'var(--wa-fg)' }}>
          {h.configSource === 'console'
            ? 'réglage lu à la Console'
            : 'réglage LOCAL — la Console n’a pas été jointe'}
        </span>
      )}

      {/* Le rôle du nœud : sur une pile, un seul agent suffit, mais une bascule doit se VOIR. */}
      {h.nodeRole && (
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>
          nœud {h.nodeRole === 'primary' ? 'primaire' : 'réplica'}
        </span>
      )}
    </div>
  );
}

/**
 * LE DÉTAIL BASE PAR BASE — la seule chose qui réponde à « CE magasin est-il sauvegardé ? ».
 *
 * ⚠️ 🔴 LES ÉCHECS D'ABORD, jamais l'ordre alphabétique. Vingt-cinq lignes vertes et une rouge au
 * quinzième rang se lisent « tout va bien » : c'est exactement le total qui masque une absence, rejoué
 * une ligne plus bas. Le tri est donc une décision d'écran, pas une commodité.
 */
function Detail({ h }: { h: BackupHost }) {
  // ⚠️ ON NE TRIE PAS LA LISTE REÇUE EN PLACE : `sort` mute, et muter la donnée du parent ferait
  //    dépendre l'affichage de l'ordre des rendus.
  const lignes = [...h.items].sort((a, b) =>
    (Number(a.ok) - Number(b.ok)) || a.database.localeCompare(b.database));
  const echecs = lignes.filter((x) => !x.ok).length;

  // ⚠️ UN AGENT QUI N'ENVOIE PAS DE DÉTAIL LE DIT (§23.0). Sans cette phrase, une liste vide se lirait
  //    « aucune base », c'est-à-dire l'alarme maximale, pour un agent qui sauvegarde très bien mais
  //    tourne encore dans une version antérieure.
  if (lignes.length === 0) {
    return (
      <div className="mono" style={{ marginTop: 8, fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>
        Cette machine n’envoie pas encore le détail base par base (agent à mettre à jour).
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div className="mono" style={{ fontSize: 'var(--t-micro)', color: echecs ? 'var(--da-fg)' : 'var(--ink500)', marginBottom: 6 }}>
        {lignes.length} base(s){echecs > 0 && ` · ${echecs} EN ÉCHEC`}
      </div>
      {/* ⚠️ LA ZONE DÉFILE, LES ÉCHECS SONT EN TÊTE : un parc de trente magasins ne doit pas repousser
          le reste de l'écran hors de vue, et ce qu'on doit voir sans défiler est déjà en haut. */}
      <div style={{ maxHeight: 260, overflowY: 'auto', display: 'grid', gap: 2 }}>
        {lignes.map((x) => (
          <div
            key={x.database}
            style={{
              display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
              padding: '3px 6px', borderRadius: 4,
              background: x.ok ? 'transparent' : 'var(--da-bg)',
            }}
          >
            <span className="mono" style={{ fontSize: 'var(--t-micro)', color: x.ok ? 'var(--ok-fg)' : 'var(--da-fg)', fontWeight: 700 }}>
              {x.ok ? '✓' : '✗'}
            </span>
            <span className="mono" style={{ fontSize: 'var(--t-meta)' }}>{x.database}</span>
            <div style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>
              {/* ⚠️ « TAILLE NON MESURÉE » N'EST PAS « 0 o » : une archive de zéro octet est une panne,
                  une taille inconnue ne l'est pas. Les confondre inventerait une alarme ou en cacherait une. */}
              {x.bytes != null ? fmtOctets(x.bytes) : 'taille non mesurée'}
            </span>
            {!x.ok && (
              // Le motif RÉEL de CETTE base (§23.0) — c'est lui qui dit où aller réparer.
              <div className="mono" style={{ flexBasis: '100%', fontSize: 'var(--t-micro)', color: 'var(--da-fg)' }}>
                {x.error ?? 'échec sans motif transmis'}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * LA LISTE DES HÔTES, telle que l'écran doit la montrer.
 *
 * ⚠️ COMPOSANT, ET NON PANNEAU AUTONOME : il ne va PAS chercher la donnée lui-même. La page
 * `Backups` la lit une fois et la lui passe — deux lectures de la même route pour un seul écran
 * feraient deux vérités possibles à l'instant où l'une des deux échoue.
 *
 * ⚠️ IL N'EXISTE QU'À UN SEUL ENDROIT. Ce panneau vivait aussi en bas de « Santé des services » ;
 * l'y laisser après la création de la page dédiée aurait fait deux écrans qui montrent le même fait
 * et divergent à la première retouche (Forme A). La supervision garde le DISQUE, les sauvegardes ont
 * leur page.
 */
export function BackupHosts({ state }: { state: BackupState }) {
  return (
    <>
      {/* ⚠️ « AUCUNE MACHINE » N'EST PAS « TOUT VA BIEN » — c'est même le pire des cas, puisqu'il
          signifie que rien n'est sauvegardé ET que personne ne le sait. */}
      {state.hosts.length === 0 && (
        <EmptyState
          icon="pulse"
          title="Aucune machine ne dépose de compte rendu"
          note="Les machines ATTENDUES se déclarent côté Console par BACKUP_EXPECTED_HOSTS — sans cette liste, une machine dont la tâche n’a jamais parlé n’apparaîtrait nulle part, et son silence se lirait « rien à signaler »."
        />
      )}

      {state.hosts.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {state.hosts.map((h) => <Hote key={h.host} h={h} />)}
        </div>
      )}
    </>
  );
}
